import { Trans, useLingui } from '@lingui/react/macro'

import { AttachmentField } from '$src/components/common/AttachmentField'
import { useAttachments } from '$src/lib/hooks/useAttachments'
import { getSeasonAndYearFromDeadline } from '$src/modules/maintenance/YearCalendarCarousel/utils'
import {
  Alert,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { categoryOptions } from '@markku/utils'
import { IconCoinEuro, IconInfoCircle } from '@tabler/icons-react'
import { TaskFormData } from './types'

interface TaskFormProps {
  data: TaskFormData
  onChange: (data: TaskFormData) => void
  errors?: Record<string, string>
  disabled?: boolean
  attachments?: ReturnType<typeof useAttachments>
  targetSeason?: string
  markAsCompleted?: boolean
  isNewTask?: boolean
  hideTitle?: boolean
}

// Season name getter moved inside component to use translations

export function TaskForm({
  data,
  onChange,
  errors,
  disabled,
  attachments,
  targetSeason,
  markAsCompleted = false,
  isNewTask = false,
  hideTitle = false,
}: TaskFormProps) {
  const { t } = useLingui()

  // Get translated season name (genitive case)
  const getSeasonName = (season: string): string => {
    const names: Record<string, string> = {
      spring: t`spring's`,
      summer: t`summer's`,
      fall: t`fall's`,
      winter: t`winter's`,
    }
    return names[season] || season
  }

  const handleFieldChange = (field: keyof TaskFormData, value: any) => {
    onChange({ ...data, [field]: value })
  }

  // Check if deadline doesn't match target season
  const deadlineSeason = data.deadline
    ? getSeasonAndYearFromDeadline(data.deadline).season
    : null
  const showSeasonMismatch =
    targetSeason && deadlineSeason && deadlineSeason !== targetSeason

  // Hide deadline when creating a new task that's already completed
  // (deadline will auto-sync with completed date in parent component)
  const showDeadlineField = !markAsCompleted || !isNewTask

  return (
    <Stack gap="md">
      {!hideTitle && (
        <TextInput
          label={<Trans>Task title</Trans>}
          placeholder={t`E.g. Window cleaning`}
          value={data.name}
          onChange={(e) => handleFieldChange('name', e.currentTarget.value)}
          error={errors?.name}
          required
          disabled={disabled}
        />
      )}

      {showDeadlineField && (
        <>
          <DateInput
            label={<Trans>Task deadline</Trans>}
            placeholder={t`Select date`}
            value={data.deadline ? new Date(data.deadline) : new Date()}
            onChange={(value) => {
              // In Mantine v8, DateInput onChange returns a date string (e.g., '2024-01-15')
              handleFieldChange('deadline', value || '')
            }}
            error={errors?.deadline}
            required
            disabled={disabled}
          />

          {showSeasonMismatch && (
            <Alert icon={<IconInfoCircle size={20} />} color="blue">
              <Text size="sm">
                <Trans>
                  Task will be added to the {getSeasonName(deadlineSeason)} list
                  based on the deadline
                </Trans>
              </Text>
            </Alert>
          )}
        </>
      )}

      <Select
        label={<Trans>Category</Trans>}
        placeholder={t`Select category`}
        data={categoryOptions}
        value={data.category}
        onChange={(value) => handleFieldChange('category', value)}
        error={errors?.category}
        clearable
        disabled={disabled}
      />

      <Textarea
        label={<Trans>Description</Trans>}
        placeholder={t`Add a detailed description of the task...`}
        value={data.description}
        onChange={(e) =>
          handleFieldChange('description', e.currentTarget.value)
        }
        error={errors?.description}
        minRows={3}
        disabled={disabled}
      />

      {(!markAsCompleted || !isNewTask) && (
        <NumberInput
          label={<Trans>Estimated cost (optional)</Trans>}
          placeholder="0"
          leftSection={<IconCoinEuro size={16} />}
          rightSection={
            <Text size="sm" c="dimmed">
              €
            </Text>
          }
          value={data.estimatedCost || ''}
          onChange={(value) => handleFieldChange('estimatedCost', value)}
          min={0}
          max={100000}
          decimalScale={2}
          disabled={disabled}
          description={t`Helps with budgeting and annual planning`}
        />
      )}

      {attachments && (
        <div>
          <Stack gap="xs">
            <TextInput
              label={<Trans>Attachments</Trans>}
              placeholder={t`Attachments are saved when the task is saved`}
              disabled
              style={{ display: 'none' }}
            />
            <AttachmentField
              attachments={attachments.attachments}
              isUploading={attachments.isUploading}
              uploadFile={attachments.uploadFile}
              removeAttachment={attachments.removeAttachment}
              disabled={disabled}
            />
          </Stack>
        </div>
      )}
    </Stack>
  )
}
